import { Card, Button } from "react-bootstrap";
import { ChevronDown, Filter, RefreshCw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Autocomplete, Checkbox, Box } from "@mui/material";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import CustomStyle, { autocompleteDropdownStyles } from "Constants/Style";
import { DateSelector } from "./Reusable/DateSelector";
import "./DashboardFilters.css";

// Warehouse option interface
export interface WarehouseOption {
  warehouse_id: string | number;
  warehouse_name: string;
  warehouse_type?: string;
  [key: string]: any;
}

// Filter state interface
export interface DashboardFiltersState {
  dateFrom: Date | undefined;
  dateTo: Date | undefined;
  warehouse: WarehouseOption[];
}

interface DashboardFiltersProps {
  dateFrom: Date | undefined;
  dateTo: Date | undefined;
  warehouse: WarehouseOption[];
  warehouseOptions: WarehouseOption[];
  onDateFromChange: (date: Date | undefined) => void;
  onDateToChange: (date: Date | undefined) => void;
  onWarehouseChange: (warehouses: WarehouseOption[]) => void;
  onReset: () => void;
  stickyEndRef?: React.RefObject<HTMLElement>;
}

export function DashboardFilters({
  dateFrom,
  dateTo,
  warehouse,
  warehouseOptions,
  onDateFromChange,
  onDateToChange,
  onWarehouseChange,
  onReset,
  stickyEndRef,
}: DashboardFiltersProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState(false);
  const [isUnsticky, setIsUnsticky] = useState(false);
  const originalTopRef = useRef<number | null>(null);
  const [isMobileView, setIsMobileView] = useState(false);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current) {
        // Store the original position on first check
        if (originalTopRef.current === null) {
          originalTopRef.current = containerRef.current.offsetTop;
          return; // Don't check sticky state on initial load
        }

        const rect = containerRef.current.getBoundingClientRect();
        const scrollY = window.scrollY || window.pageYOffset;
        const stickyThreshold = 70;
        
        // Check if we've scrolled past the end boundary
        let hasScrolledPastEnd = false;
        if (stickyEndRef?.current) {
          const endRect = stickyEndRef.current.getBoundingClientRect();
          // If the end element is above the sticky threshold, we've scrolled past it
          hasScrolledPastEnd = endRect.top < stickyThreshold;
        }
        
        // Only consider it sticky if:
        // 1. The element's top is at the sticky position (70px)
        // 2. AND we've scrolled past the original position (meaning it's actually stuck)
        // 3. AND we haven't scrolled past the end boundary
        const isAtStickyPosition = Math.abs(rect.top - stickyThreshold) < 2;
        const hasScrolledPastOriginal = scrollY > (originalTopRef.current - stickyThreshold);
        const shouldBeSticky = isAtStickyPosition && hasScrolledPastOriginal && !hasScrolledPastEnd;
        
        setIsSticky(shouldBeSticky);
        setIsUnsticky(hasScrolledPastEnd);
      }
    };

    // Wait for next frame to ensure DOM is fully rendered
    const timeoutId = setTimeout(() => {
      handleScroll(); // This will set originalTopRef but not check sticky state
      window.addEventListener("scroll", handleScroll, { passive: true });
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [stickyEndRef]);

  useEffect(() => {
    const MOBILE_BREAKPOINT = 992;

    const handleResize = () => {
      const isMobile = typeof window !== "undefined" && window.innerWidth <= MOBILE_BREAKPOINT;
      setIsMobileView(isMobile);
      // Always show filters again when returning to desktop
      if (!isMobile) {
        setIsMobileFiltersOpen(true);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const getOptionLabel = (option: WarehouseOption) => {
    return option?.warehouse_name || "";
  };

  const isOptionEqualToValue = (option: WarehouseOption, value: WarehouseOption) => {
    return option?.warehouse_id === value?.warehouse_id;
  };

  // Handle warehouse selection change - simple: just pass through the selection
  const handleWarehouseChange = (newValue: WarehouseOption[]) => {
    onWarehouseChange(newValue);
  };

  const isContentHidden = isMobileView && !isMobileFiltersOpen;

  return (
    <Card 
      ref={containerRef}
      className={`dashboard-filters-container ${isSticky ? "dashboard-filters-sticky" : ""} ${isUnsticky ? "dashboard-filters-unsticky" : ""}`}
    >
      <div className="dashboard-filters-wrapper d-flex flex-column flex-lg-row align-items-stretch align-items-lg-center flex-wrap gap-2 gap-lg-3">
        {/* Filter Label */}
        <div className="dashboard-filters-label">
          <div className="dashboard-filters-label-content">
            <Filter className="dashboard-filters-label-icon" />
            <span className="dashboard-filters-label-text">Filters</span>
          </div>
          {isMobileView && (
            <Button
              variant="outline"
              size="sm"
              className="dashboard-filters-toggle-button"
              onClick={() => setIsMobileFiltersOpen((prev) => !prev)}
              aria-expanded={isMobileFiltersOpen}
              aria-controls="dashboard-filters-content"
            >
              {isMobileFiltersOpen ? "Hide" : "Show"}
              <ChevronDown
                className={`dashboard-filters-toggle-icon ${isMobileFiltersOpen ? "expanded" : ""}`}
              />
            </Button>
          )}
        </div>

        <div
          id="dashboard-filters-content"
          className={`dashboard-filters-content ${
            isContentHidden ? "d-none" : "d-flex"
          } flex-column flex-lg-row align-items-stretch align-items-lg-center flex-wrap gap-2 gap-lg-3 flex-fill`}
        >
          {/* Date Range Container */}
          <div className="dashboard-filters-date-container d-flex flex-row align-items-center gap-2 flex-fill">
            {/* Date Range - From */}
            <div className="dashboard-filters-date-group d-flex flex-row align-items-center gap-2">
              <span className="dashboard-filters-date-label">From:</span>
              <div className="dashboard-filters-datepicker-wrapper flex-fill">
                <DateSelector
                  selected={dateFrom || null}
                  onChange={(date) => onDateFromChange(date || undefined)}
                  placeholder="Select date"
                  dateFormat="dd MMM yyyy"
                  maxDate={dateTo || new Date()}
                  customInputClassName="dashboard-filters-date-button"
                />
              </div>
            </div>

            {/* Date Range - To */}
            <div className="dashboard-filters-date-group d-flex flex-row align-items-center gap-2">
              <span className="dashboard-filters-date-label">To:</span>
              <div className="dashboard-filters-datepicker-wrapper flex-fill">
                <DateSelector
                  selected={dateTo || null}
                  onChange={(date) => onDateToChange(date || undefined)}
                  placeholder="Select date"
                  dateFormat="dd MMM yyyy"
                  minDate={dateFrom}
                  maxDate={new Date()}
                  customInputClassName="dashboard-filters-date-button"
                />
              </div>
            </div>
          </div>

          {/* Warehouse and Reset Container */}
          <div className="dashboard-filters-warehouse-reset-container d-flex flex-column flex-lg-row align-items-stretch align-items-lg-center gap-2 flex-fill">
            <div className="dashboard-filters-warehouse-wrapper flex-fill">
              <Autocomplete
                multiple
                options={warehouseOptions}
                value={warehouse}
                onChange={(event, newValue) => {
                  handleWarehouseChange(newValue);
                }}
                getOptionLabel={getOptionLabel}
                isOptionEqualToValue={isOptionEqualToValue}
                disableClearable={false}
                disableCloseOnSelect={true} // Keep dropdown open after selection
                renderTags={() => null} // Hide chips in input box
                sx={{
                  ...CustomStyle.multiselect,
                  minWidth: "220px",
                  width: "100%",
                  '& .MuiInputBase-root': {
                    height: '40px',
                    fontSize: '13px',
                    fontFamily: 'Poppins, sans-serif',
                  },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0.5rem',
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#65A19D',
                      borderWidth: '1.5px',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#65A19D',
                    },
                  },
                }}
                renderOption={(props, option, { selected }) => {
                  const { key, ...rest } = props;
                  return (
                    <li
                      key={key}
                      {...rest}
                      style={{
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: '13px',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                          gap: 1,
                        }}
                      >
                        <Checkbox
                          checked={selected}
                          sx={{
                            color: '#65A19D',
                            '&.Mui-checked': {
                              color: '#65A19D',
                            },
                            padding: '4px',
                          }}
                        />
                        <span>{getOptionLabel(option)}</span>
                      </Box>
                    </li>
                  );
                }}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderInput={(params) => (
                  <BootstrapTextField
                    {...params}
                    placeholder={
                      warehouse.length > 0
                        ? `${warehouse.length} warehouse${warehouse.length > 1 ? 's' : ''} selected`
                        : "All Warehouses"
                    }
                    sx={autocompleteDropdownStyles.inputSx}
                  />
                )}
              />
            </div>
            <div className="dashboard-filters-reset-wrapper">
              <Button
                variant="outline"
                size="sm"
                className="dashboard-filters-action-button"
                onClick={onReset}
              >
                <RefreshCw className="dashboard-filters-action-icon" />
                Reset
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}